import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../shell/backofficeSidebar';
import { useAuth } from '../../../../context/AuthContext';
import { useCurrency } from '../../../../context/CurrencyContext';
import { PaginatedListLoading } from '../../../../components/ui/PaginatedListLoading';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:3001';

/** One row per product from GET /inventory/stock-summary */
type StockSummaryRow = {
  id: string;
  sku: string;
  name: string;
  updatedAt: string;
  imageUrl?: string | null;
  baseUnit: string;
  currentStockLbs: number;
  pricePerUnit: number;
  salePrice?: number | null;
  categoryId?: string | null;
  category?: {
    id: string;
    name: string;
    slug: string;
    department?: { id: string; name: string; slug: string } | null;
  } | null;
};

type LotRow = {
  id: string;
  batchCode?: string | null;
  purchasedQtyLbs: number;
  remainingQtyLbs: number;
  expiryDate?: string | null;
  product: {
    id: string;
    sku: string;
    name: string;
  };
};

type ExpiringLotRow = {
  id: string;
  batchCode?: string | null;
  remainingQtyLbs: number;
  expiryDate?: string | null;
  product: {
    id: string;
    sku: string;
    name: string;
  };
};

type PurchaseItemRow = {
  id: string;
  quantityLbs: number;
  unit: 'LB' | 'KG' | 'PACK';
  unitPrice: number;
  lineTotal: number;
  expiryDate?: string | null;
  lot?: { batchCode?: string | null } | null;
  product: {
    id: string;
    sku: string;
    name: string;
    category?: {
      id: string;
      name: string;
      slug: string;
      department?: { id: string; name: string; slug: string } | null;
    } | null;
  };
};

type PurchaseRow = {
  id: string;
  reference?: string | null;
  supplierName?: string | null;
  purchasedAt: string;
  totalCost: number;
  items: PurchaseItemRow[];
};

type PaginatedStockSummary = {
  items: StockSummaryRow[];
  total: number;
  page: number;
  pageSize: number;
  totalWeightLbs: number;
  totalPurchaseCost: number;
  totalStockSaleValue: number;
};

type PaginatedLots = {
  items: LotRow[];
  total: number;
  page: number;
  pageSize: number;
};

type PaginatedPurchases = {
  items: PurchaseRow[];
  total: number;
  page: number;
  pageSize: number;
  totalCostSum: number;
};

type SupplierOption = {
  id: string;
  name: string;
  phone?: string | null;
  country?: string | null;
};

type PurchaseLineUnit = 'LB' | 'KG' | 'PACK';

function getCountryName(code?: string | null): string {
  if (!code) return '';
  try {
    if (typeof Intl !== 'undefined' && (Intl as any).DisplayNames) {
      const locale = navigator.language || 'en';
      const displayNames = new (Intl as any).DisplayNames([locale, 'en'], {
        type: 'region',
      });
      const name = displayNames.of(code);
      if (typeof name === 'string') return name;
    }
  } catch {
    // ignore and fallback
  }
  return code;
}

function InventoryPage() {
  const { authorizedFetch } = useAuth();
  const { currency, formatPrice } = useCurrency();
  const { t } = useTranslation();
  const location = useLocation();
  const [workspaceScope, setWorkspaceScope] = useState<string>(() => {
    if (typeof window === 'undefined') return '__all__';
    return window.localStorage.getItem('workspace_department_scope') || '__all__';
  });
  const [, setWorkspaceDepartments] = useState<
    Array<{ id: string; slug: string; name: string }>
  >([]);
  // Derive which view to show purely from the current route:
  // - /workspace/inventory -> stock view
  // - /workspace/purchases -> purchase history view
  const tab: 'stock' | 'purchases' = location.pathname.endsWith('/purchases')
    ? 'purchases'
    : 'stock';

  const [stockSummary, setStockSummary] = useState<StockSummaryRow[]>([]);
  const [stockTotal, setStockTotal] = useState(0);
  const [stockPage, setStockPage] = useState(1);
  const [stockSearch, setStockSearch] = useState('');
  const [stockLoading, setStockLoading] = useState(true);
  const [stockTotalWeightLbs, setStockTotalWeightLbs] = useState(0);
  const [stockTotalPurchaseCost, setStockTotalPurchaseCost] = useState(0);
  const [stockTotalStockSaleValue, setStockTotalStockSaleValue] = useState(0);
  const [lotsForProduct, setLotsForProduct] = useState<LotRow[]>([]);
  const [lotsModalProduct, setLotsModalProduct] = useState<{ id: string; name: string; sku: string } | null>(null);
  const [lotsModalLoading, setLotsModalLoading] = useState(false);
  const [editingLotExpiryId, setEditingLotExpiryId] = useState<string | null>(null);
  const [editingLotExpiryValue, setEditingLotExpiryValue] = useState('');
  const [lotExpirySaving, setLotExpirySaving] = useState(false);
  const [editingSalePriceId, setEditingSalePriceId] = useState<string | null>(null);
  const [editingSalePriceValue, setEditingSalePriceValue] = useState('');
  const [salePriceSaving, setSalePriceSaving] = useState(false);
  const [openStockCategoryId, setOpenStockCategoryId] = useState<string | null>(null);
  const [expiryDaysBefore, setExpiryDaysBefore] = useState(7);
  const [expiringSoon, setExpiringSoon] = useState<{
    loading: boolean;
    error: string | null;
    productsCount: number;
    products: Array<{
      productId: string;
      sku: string;
      name: string;
      earliestExpiry: string | null;
      lotsCount: number;
      remainingQtyLbs: number;
    }>;
  }>({
    loading: false,
    error: null,
    productsCount: 0,
    products: [],
  });
  const [showExpiringSoon, setShowExpiringSoon] = useState(false);

  const [purchases, setPurchases] = useState<PurchaseRow[]>([]);
  const [purchasesTotal, setPurchasesTotal] = useState(0);
  const [purchasesTotalCostSum, setPurchasesTotalCostSum] = useState(0);
  const [purchasesPage, setPurchasesPage] = useState(1);
  const [purchasesSearch, setPurchasesSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [purchasesLoading, setPurchasesLoading] = useState(true);
  const [openPurchaseDate, setOpenPurchaseDate] = useState<string | null>(null);
  const [openPurchaseId, setOpenPurchaseId] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [showNewPurchase, setShowNewPurchase] = useState(false);
  const [newPurchaseSaving, setNewPurchaseSaving] = useState(false);
  const [newPurchaseError, setNewPurchaseError] = useState<string | null>(null);
  const [newPurchaseErrors, setNewPurchaseErrors] = useState<{
    supplier?: string;
    purchaseDate?: string;
    product?: string;
    quantity?: string;
    unit?: string;
    unitPrice?: string;
  }>({});
  const [supplierQuery, setSupplierQuery] = useState('');
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [isSelfSupplier, setIsSelfSupplier] = useState(false);
  const [purchaseDate, setPurchaseDate] = useState('');
  const [lineProductId, setLineProductId] = useState('');
  const [lineQty, setLineQty] = useState('');
  const [lineUnit, setLineUnit] = useState<PurchaseLineUnit>('LB');
  const [linePrice, setLinePrice] = useState('');
  const [lineExpiry, setLineExpiry] = useState('');
  const [lineBatch, setLineBatch] = useState('');
  const [purchaseLines, setPurchaseLines] = useState<Array<{
    productId: string;
    productName: string;
    quantity: number;
    unit: PurchaseLineUnit;
    unitPrice: number;
    expiryDate?: string;
    batchCode?: string;
  }>>([]);
  const [departmentUnits, setDepartmentUnits] = useState<PurchaseLineUnit[]>(['LB', 'KG']);

  const isAllScope = React.useMemo(() => {
    const scope = String(workspaceScope || '').trim().toLowerCase();
    return !scope || scope === '__all__';
  }, [workspaceScope]);
  const scopedDepartmentSlug = React.useMemo(() => {
    if (isAllScope) return '';
    return String(workspaceScope || '').trim().toLowerCase();
  }, [isAllScope, workspaceScope]);
  const [duplicatePick, setDuplicatePick] = useState<{
    open: boolean;
    product: { id: string; name: string; sku: string } | null;
  }>({ open: false, product: null });

  const [productQuery, setProductQuery] = useState('');
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [productOptions, setProductOptions] = useState<Array<{ id: string; name: string; sku: string }>>([]);
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
  const [supplierOptions, setSupplierOptions] = useState<SupplierOption[]>([]);

  const switchWorkspaceScope = React.useCallback((nextDepartmentSlug: string) => {
    const slug = String(nextDepartmentSlug || '').trim().toLowerCase();
    if (!slug || typeof window === 'undefined') return;
    window.localStorage.setItem('workspace_department_scope', slug);
    window.dispatchEvent(
      new CustomEvent('workspace-department-scope:change', {
        detail: { scope: slug },
      }),
    );
  }, []);

  const loadStockSummary = React.useCallback(async (pageToLoad: number, q: string) => {
    setStockLoading(true);
    setError(null);
    try {
      const url = new URL(`${API_BASE}/inventory/stock-summary`);
      url.searchParams.set('page', String(pageToLoad));
      url.searchParams.set('pageSize', '20');
      if (q) url.searchParams.set('q', q);
      if (scopedDepartmentSlug) {
        url.searchParams.set('departmentSlug', scopedDepartmentSlug);
      }
      const res = await authorizedFetch(url.toString());
      if (!res.ok) {
        throw new Error('Unable to load stock summary.');
      }
      const data = (await res.json()) as PaginatedStockSummary;
      setStockSummary(data.items);
      setStockTotal(data.total);
      setStockPage(data.page);
      setStockTotalWeightLbs(Number((data as any).totalWeightLbs ?? 0));
      setStockTotalPurchaseCost(Number((data as any).totalPurchaseCost ?? 0));
      setStockTotalStockSaleValue(Number((data as any).totalStockSaleValue ?? 0));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load stock summary.');
    } finally {
      setStockLoading(false);
    }
  }, [authorizedFetch, scopedDepartmentSlug]);

  const openLotsModal = async (product: { id: string; name: string; sku: string }) => {
    setLotsModalProduct(product);
    setLotsForProduct([]);
    setLotsModalLoading(true);
    try {
      const url = new URL(`${API_BASE}/inventory/lots`);
      url.searchParams.set('productId', product.id);
      url.searchParams.set('pageSize', '100');
      if (scopedDepartmentSlug) {
        url.searchParams.set('departmentSlug', scopedDepartmentSlug);
      }
      const res = await authorizedFetch(url.toString());
      if (!res.ok) return;
      const data = (await res.json()) as PaginatedLots;
      setLotsForProduct(data.items);
    } finally {
      setLotsModalLoading(false);
    }
  };

  const saveLotExpiry = async (lotId: string, value: string) => {
    setLotExpirySaving(true);
    try {
      const body = {
        expiryDate: value === '' ? null : new Date(value).toISOString(),
      };
      const res = await authorizedFetch(`${API_BASE}/inventory/lots/${lotId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to update expiry date');
      setEditingLotExpiryId(null);
      setEditingLotExpiryValue('');
      if (lotsModalProduct) {
        await openLotsModal(lotsModalProduct);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update expiry date');
    } finally {
      setLotExpirySaving(false);
    }
  };

  const setSalePrice = async (productId: string, value: string) => {
    setSalePriceSaving(true);
    try {
      const num = value === '' ? null : Number(value);
      const res = await authorizedFetch(`${API_BASE}/catalog/products/${productId}/sale-price`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ salePrice: num }),
      });
      if (!res.ok) throw new Error('Failed to update sale price');
      setEditingSalePriceId(null);
      setEditingSalePriceValue('');
      await loadStockSummary(stockPage, stockSearch);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update sale price');
    } finally {
      setSalePriceSaving(false);
    }
  };

  const loadPurchases = React.useCallback(async (pageToLoad: number, q: string, from: string, to: string) => {
    setPurchasesLoading(true);
    setError(null);
    try {
      const url = new URL(`${API_BASE}/inventory/purchases`);
      url.searchParams.set('page', String(pageToLoad));
      url.searchParams.set('pageSize', '20');
      if (q) url.searchParams.set('q', q);
      if (from) url.searchParams.set('from', from);
      if (to) url.searchParams.set('to', to);
      if (scopedDepartmentSlug) {
        url.searchParams.set('departmentSlug', scopedDepartmentSlug);
      }
      const res = await authorizedFetch(url.toString());
      if (!res.ok) {
        throw new Error('Unable to load purchases.');
      }
      const data = (await res.json()) as PaginatedPurchases;
      setPurchases(data.items);
      setPurchasesTotal(data.total);
      setPurchasesPage(data.page);
      setPurchasesTotalCostSum(Number((data as any).totalCostSum ?? 0));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load purchases.');
    } finally {
      setPurchasesLoading(false);
    }
  }, [authorizedFetch, scopedDepartmentSlug]);

  useEffect(() => {
    const readScope = () =>
      (typeof window !== 'undefined'
        ? window.localStorage.getItem('workspace_department_scope')
        : null) || '__all__';
    const handleScopeChange = () => {
      setWorkspaceScope(readScope());
    };
    window.addEventListener(
      'workspace-department-scope:change',
      handleScopeChange as EventListener,
    );
    window.addEventListener('storage', handleScopeChange);
    return () => {
      window.removeEventListener(
        'workspace-department-scope:change',
        handleScopeChange as EventListener,
      );
      window.removeEventListener('storage', handleScopeChange);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authorizedFetch(`${API_BASE}/departments/workspace`);
        if (!res.ok || cancelled) return;
        const rows = (await res.json()) as Array<{ id?: string; slug?: string; name?: string }>;
        if (!cancelled) {
          setWorkspaceDepartments(
            (Array.isArray(rows) ? rows : [])
              .map((row) => ({
                id: String(row.id || '').trim(),
                slug: String(row.slug || '').trim().toLowerCase(),
                name: String(row.name || '').trim(),
              }))
              .filter((row) => Boolean(row.id && row.slug)),
          );
        }
      } catch {
        if (!cancelled) setWorkspaceDepartments([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch]);

  useEffect(() => {
    void loadPurchases(1, '', '', '');
  }, [loadPurchases]);

  useEffect(() => {
    if (tab !== 'stock') return;
    let cancelled = false;
    const run = async () => {
      setExpiringSoon((p) => ({ ...p, loading: true, error: null }));
      try {
        // Read daysBeforeExpiry from the admin setting (Expiry alerts config)
        let days = 7;
        const cfgRes = await authorizedFetch(`${API_BASE}/expiry-alert/config`);
        if (cfgRes.ok) {
          const cfg = (await cfgRes.json()) as { daysBeforeExpiry?: number };
          if (typeof cfg.daysBeforeExpiry === 'number' && Number.isFinite(cfg.daysBeforeExpiry)) {
            days = Math.max(1, Math.min(365, Math.floor(cfg.daysBeforeExpiry)));
          }
        }
        if (cancelled) return;
        setExpiryDaysBefore(days);

        // Preview lots expiring within days; we’ll group into products.
        const prevRes = await authorizedFetch(`${API_BASE}/expiry-alert/preview?days=${days}`);
        if (!prevRes.ok) {
          // If not allowed, just hide counts silently.
          if (!cancelled) {
            setExpiringSoon({ loading: false, error: null, productsCount: 0, products: [] });
          }
          return;
        }
        const lots = (await prevRes.json()) as ExpiringLotRow[];
        if (cancelled) return;

        const byProduct = new Map<string, {
          productId: string;
          sku: string;
          name: string;
          earliestExpiry: string | null;
          lotsCount: number;
          remainingQtyLbs: number;
        }>();

        for (const l of lots || []) {
          const p = l.product;
          if (!p?.id) continue;
          const key = p.id;
          const hit = byProduct.get(key) ?? {
            productId: p.id,
            sku: p.sku,
            name: p.name,
            earliestExpiry: null,
            lotsCount: 0,
            remainingQtyLbs: 0,
          };
          hit.lotsCount += 1;
          hit.remainingQtyLbs += Number(l.remainingQtyLbs || 0);
          if (l.expiryDate) {
            if (!hit.earliestExpiry || new Date(l.expiryDate) < new Date(hit.earliestExpiry)) {
              hit.earliestExpiry = l.expiryDate;
            }
          }
          byProduct.set(key, hit);
        }

        const products = Array.from(byProduct.values()).sort((a, b) => {
          const ad = a.earliestExpiry ? new Date(a.earliestExpiry).getTime() : Number.POSITIVE_INFINITY;
          const bd = b.earliestExpiry ? new Date(b.earliestExpiry).getTime() : Number.POSITIVE_INFINITY;
          return ad - bd || b.remainingQtyLbs - a.remainingQtyLbs || a.name.localeCompare(b.name);
        });

        setExpiringSoon({
          loading: false,
          error: null,
          productsCount: products.length,
          products,
        });
      } catch (err) {
        if (!cancelled) {
          setExpiringSoon({
            loading: false,
            error: err instanceof Error ? err.message : 'Unable to load expiring soon products.',
            productsCount: 0,
            products: [],
          });
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, tab]);

  // Default: first purchase date group opened; keep accordion valid after filtering/reload.
  useEffect(() => {
    if (!purchases.length) {
      setOpenPurchaseDate(null);
      setOpenPurchaseId(null);
      return;
    }
    const firstKey = new Date(purchases[0].purchasedAt).toLocaleDateString();
    setOpenPurchaseDate((prev) => {
      if (!prev) return firstKey;
      const exists = purchases.some((p) => new Date(p.purchasedAt).toLocaleDateString() === prev);
      return exists ? prev : firstKey;
    });
    setOpenPurchaseId((prev) => {
      if (!prev) return null;
      const exists = purchases.some((p) => p.id === prev);
      return exists ? prev : null;
    });
  }, [purchases]);

  // Real-time debounced search for stock (no button click; triggers as user types)
  useEffect(() => {
    const timeout = setTimeout(() => {
      void loadStockSummary(1, stockSearch);
    }, 280);
    return () => clearTimeout(timeout);
  }, [stockSearch, loadStockSummary]);

  // Debounced supplier search for the purchase form
  useEffect(() => {
    if (!supplierQuery || isSelfSupplier || supplierId) {
      setSupplierOptions([]);
      return;
    }
    let cancelled = false;
    const timeout = setTimeout(async () => {
      setSupplierSearchLoading(true);
      try {
        const url = new URL(`${API_BASE}/suppliers`);
        url.searchParams.set('page', '1');
        url.searchParams.set('pageSize', '20');
        url.searchParams.set('q', supplierQuery);
        url.searchParams.set('activeOnly', 'true');
        const res = await authorizedFetch(url.toString());
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as {
          items: { id: string; name: string; phone?: string | null; country?: string | null }[];
        };
        setSupplierOptions(
          data.items.map((s) => ({
            id: s.id,
            name: s.name,
            phone: s.phone ?? null,
            country: s.country ?? null,
          })),
        );
      } catch {
        if (!cancelled) {
          setSupplierOptions([]);
        }
      } finally {
        if (!cancelled) {
          setSupplierSearchLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [authorizedFetch, supplierQuery, isSelfSupplier, supplierId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!scopedDepartmentSlug) {
        setDepartmentUnits(['LB', 'KG']);
        setLineUnit((prev) => (prev === 'PACK' ? 'LB' : prev));
        return;
      }
      try {
        const res = await fetch(
          `${API_BASE}/departments/public/${encodeURIComponent(scopedDepartmentSlug)}`,
        );
        if (!res.ok || cancelled) return;
        const dep = (await res.json()) as {
          productUnitLabels?: string[];
          defaultProductUnit?: PurchaseLineUnit;
        };
        const labels = Array.isArray(dep.productUnitLabels) ? dep.productUnitLabels : [];
        const normalized = labels.map((x) => String(x || '').trim().toLowerCase());
        const next = new Set<PurchaseLineUnit>();
        if (normalized.some((x) => ['lb', 'lbs', 'pound', 'pounds'].includes(x))) next.add('LB');
        if (normalized.some((x) => ['kg', 'kgs', 'kilogram', 'kilograms'].includes(x))) next.add('KG');
        if (normalized.some((x) => ['pack', 'packs', 'package', 'packages'].includes(x))) next.add('PACK');
        if (dep.defaultProductUnit) next.add(dep.defaultProductUnit);
        if (next.size === 0) {
          next.add('LB');
          next.add('KG');
        }
        const options = Array.from(next);
        if (!cancelled) {
          setDepartmentUnits(options);
          setLineUnit((prev) => (options.includes(prev) ? prev : options[0]));
        }
      } catch {
        if (!cancelled) {
          setDepartmentUnits(['LB', 'KG']);
          setLineUnit((prev) => (prev === 'PACK' ? 'LB' : prev));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scopedDepartmentSlug]);

  // Debounced product search for purchase lines (uses stock summary endpoint)
  useEffect(() => {
    if (!productQuery.trim()) {
      setProductOptions([]);
      return;
    }
    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      setProductSearchLoading(true);
      try {
        const url = new URL(`${API_BASE}/inventory/stock-summary`);
        url.searchParams.set('page', '1');
        url.searchParams.set('pageSize', '20');
        url.searchParams.set('q', productQuery.trim());
        if (scopedDepartmentSlug) {
          url.searchParams.set('departmentSlug', scopedDepartmentSlug);
        }
        const res = await authorizedFetch(url.toString());
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as PaginatedStockSummary;
        if (cancelled) return;
        setProductOptions(
          (data.items || []).map((p) => ({ id: p.id, name: p.name, sku: p.sku })),
        );
      } catch {
        if (!cancelled) setProductOptions([]);
      } finally {
        if (!cancelled) setProductSearchLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [authorizedFetch, productQuery, scopedDepartmentSlug]);

  const stockTotalPages = Math.max(1, Math.ceil(stockTotal / 20));
  const purchasesTotalPages = Math.max(1, Math.ceil(purchasesTotal / 20));

  const stockCategories = stockSummary.reduce<Record<string, {
    id: string;
    name: string;
    count: number;
    totalWeightLbs: number;
    totalValue: number;
    products: StockSummaryRow[];
    latestUpdatedAt: string | null;
  }>>((acc, row) => {
    const id = row.category?.id || '__uncat__';
    const name = row.category?.name || 'Uncategorized';
    if (!acc[id]) {
      acc[id] = {
        id,
        name,
        count: 0,
        totalWeightLbs: 0,
        totalValue: 0,
        products: [],
        latestUpdatedAt: null,
      };
    }
    const grp = acc[id];
    grp.count += 1;
    grp.totalWeightLbs += Number(row.currentStockLbs || 0);
    const effectiveSalePrice = row.salePrice ?? row.pricePerUnit;
    grp.totalValue += Number(row.currentStockLbs || 0) * Number(effectiveSalePrice || 0);
    grp.products.push(row);
    if (row.updatedAt) {
      if (!grp.latestUpdatedAt || new Date(row.updatedAt) > new Date(grp.latestUpdatedAt)) {
        grp.latestUpdatedAt = row.updatedAt;
      }
    }
    return acc;
  }, {});

  const stockCategoryList = Object.values(stockCategories).sort(
    (a, b) => b.totalValue - a.totalValue || a.name.localeCompare(b.name),
  );
  const stockDepartmentsGrouped = React.useMemo(() => {
    const groups = new Map<
      string,
      {
        departmentId: string;
        departmentName: string;
        departmentSlug: string;
        productsCount: number;
        totalWeightLbs: number;
        totalStockSaleValue: number;
        rows: StockSummaryRow[];
      }
    >();
    stockSummary.forEach((row) => {
      const dep = row.category?.department;
      const depId = String(dep?.id || '__unknown__');
      const depName = dep?.name || 'Unknown department';
      const depSlug = dep?.slug || 'unknown';
      const price = Number(row.salePrice ?? row.pricePerUnit ?? 0);
      const weight = Number(row.currentStockLbs || 0);
      const existing = groups.get(depId);
      if (existing) {
        existing.productsCount += 1;
        existing.totalWeightLbs += weight;
        existing.totalStockSaleValue += weight * price;
        existing.rows.push(row);
      } else {
        groups.set(depId, {
          departmentId: depId,
          departmentName: depName,
          departmentSlug: depSlug,
          productsCount: 1,
          totalWeightLbs: weight,
          totalStockSaleValue: weight * price,
          rows: [row],
        });
      }
    });
    return Array.from(groups.values())
      .map((g) => ({
        ...g,
        rows: g.rows
          .slice()
          .sort((a, b) => b.currentStockLbs - a.currentStockLbs || a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.departmentName.localeCompare(b.departmentName));
  }, [stockSummary]);
  const purchaseDepartmentsGrouped = React.useMemo(() => {
    const groups = new Map<
      string,
      {
        departmentId: string;
        departmentName: string;
        departmentSlug: string;
        purchasesCount: number;
        totalPurchaseCost: number;
        rows: PurchaseRow[];
      }
    >();
    purchases.forEach((purchase) => {
      const dep =
        purchase.items?.[0]?.product?.category?.department
        || null;
      const depId = String(dep?.id || '__unknown__');
      const depName = dep?.name || 'Unknown department';
      const depSlug = dep?.slug || 'unknown';
      const existing = groups.get(depId);
      if (existing) {
        existing.purchasesCount += 1;
        existing.totalPurchaseCost += Number(purchase.totalCost || 0);
        existing.rows.push(purchase);
      } else {
        groups.set(depId, {
          departmentId: depId,
          departmentName: depName,
          departmentSlug: depSlug,
          purchasesCount: 1,
          totalPurchaseCost: Number(purchase.totalCost || 0),
          rows: [purchase],
        });
      }
    });
    return Array.from(groups.values())
      .map((g) => ({
        ...g,
        rows: g.rows.slice().sort((a, b) => new Date(b.purchasedAt).getTime() - new Date(a.purchasedAt).getTime()),
      }))
      .sort((a, b) => a.departmentName.localeCompare(b.departmentName));
  }, [purchases]);

  const handleNewPurchaseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewPurchaseSaving(true);
    setNewPurchaseError(null);
    setNewPurchaseErrors({});
    try {
      const errors: {
        supplier?: string;
        purchaseDate?: string;
        product?: string;
        quantity?: string;
        unit?: string;
        unitPrice?: string;
      } = {};

      if (!isSelfSupplier && !supplierId) {
        errors.supplier = t(
          'admin.inventory_new_purchase_supplier_required',
          'Please select a supplier or mark as self-supplier.',
        );
      }
      if (!purchaseDate) {
        errors.purchaseDate = t(
          'admin.inventory_new_purchase_date_required',
          'Please select a purchase date.',
        );
      }
      if (!purchaseLines.length) {
        errors.product = t('admin.inventory_new_purchase_product_required', 'Add at least one item line.');
      }

      if (Object.keys(errors).length > 0) {
        setNewPurchaseErrors(errors);
        throw new Error(
          t(
            'admin.inventory_new_purchase_validation_error',
            'Please fix the errors in the form before saving.',
          ),
        );
      }
      const body = {
        supplierId: isSelfSupplier ? null : supplierId,
        isSelfSupplier,
        purchasedAt: new Date(purchaseDate).toISOString(),
        lines: purchaseLines.map((l) => ({
          productId: l.productId,
          quantity: l.quantity,
          unit: l.unit,
          unitPrice: l.unitPrice,
          expiryDate: l.expiryDate ? new Date(l.expiryDate).toISOString() : undefined,
          batchCode: l.batchCode || undefined,
        })),
      };
      const res = await authorizedFetch(`${API_BASE}/inventory/purchases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const payload = data as { message?: string; code?: string; redirectPath?: string };
        let message =
          payload.message ||
          t(
            'admin.inventory_new_purchase_error',
            'Unable to create purchase.',
          );
        if (payload.code === 'BUDGET_PERIOD_REQUIRED') {
          message = t(
            'admin.inventory_new_purchase_budget_required',
            'This purchase is blocked because there is no active budget period for the selected date. Please create and activate a budget period first in the Budget module.',
          );
        }
        throw new Error(message);
      }
      setShowNewPurchase(false);
      setSupplierQuery('');
      setSupplierId(null);
      setIsSelfSupplier(false);
      setPurchaseDate('');
      setLineProductId('');
      setLineQty('');
      setLineUnit(departmentUnits[0] || 'LB');
      setLinePrice('');
      setLineExpiry('');
      setLineBatch('');
      setPurchaseLines([]);
      setProductQuery('');
      setProductOptions([]);
      await loadStockSummary(1, stockSearch);
      await loadPurchases(1, purchasesSearch, fromDate, toDate);
    } catch (err) {
      setNewPurchaseError(
        err instanceof Error
          ? err.message
          : t('backoffice.inventory_new_purchase_error', 'Unable to create purchase.'),
      );
    } finally {
      setNewPurchaseSaving(false);
    }
  };

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={location.pathname}
    >
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">
            {t('backoffice.inventory_title', 'Inventory & Stock Control')}
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            {t('backoffice.inventory_subtitle', 'Track stock lots, expiry dates and purchase history with fast search and filters.')}
          </p>
        </section>

        {error && (
          <p className="text-xs text-red-500">
            {error}
          </p>
        )}

        {tab === 'stock' ? (
          <section className="workspace-panel">
            <div className="mb-4 space-y-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <input
                  type="search"
                  placeholder={t('backoffice.inventory_stock_search', 'Search by product name or SKU (live)')}
                  value={stockSearch}
                  onChange={(e) => setStockSearch(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70 sm:max-w-sm"
                />
                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => setShowExpiringSoon(true)}
                    className={[
                      'rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wide shadow-sm transition',
                      expiringSoon.productsCount > 0
                        ? 'bg-rose-600 text-white hover:bg-rose-500'
                        : 'border border-slate-300 bg-white text-slate-700 hover:border-slate-400',
                    ].join(' ')}
                    title={`Products expiring within ${expiryDaysBefore} day(s)`}
                  >
                    Expiring soon ({expiryDaysBefore}d)
                    <span
                      className={[
                        'ml-2 inline-flex min-w-[22px] items-center justify-center rounded-full px-2 py-0.5 text-[11px] font-bold',
                        expiringSoon.productsCount > 0 ? 'bg-white/15' : 'bg-slate-900/5 text-slate-700',
                      ].join(' ')}
                    >
                      {expiringSoon.loading ? '…' : expiringSoon.productsCount}
                    </span>
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap items-stretch gap-3">
                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Products
                    </div>
                    <div className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
                      {stockTotal}
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Total weight
                    </div>
                    <div className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
                      {stockTotalWeightLbs.toFixed(2)} <span className="text-base font-semibold text-slate-500">lb</span>
                    </div>
                  </div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 shadow-sm">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                      Stock purchase cost
                    </div>
                    <div className="mt-1 text-2xl font-semibold tracking-tight text-emerald-900 sm:text-3xl">
                      {formatPrice(stockTotalPurchaseCost)}
                    </div>
                    <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-800/80">
                      What you paid suppliers (COGS basis)
                    </div>
                  </div>
                  <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 shadow-sm">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700">
                      Stock sale value
                    </div>
                    <div className="mt-1 text-2xl font-semibold tracking-tight text-indigo-900 sm:text-3xl">
                      {formatPrice(stockTotalStockSaleValue)}
                    </div>
                    <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-indigo-800/80">
                      Total stock weight × sale price per unit
                    </div>
                  </div>
                </div>
            </div>

            {stockLoading ? (
              <PaginatedListLoading
                title={t('backoffice.inventory_stock_loading', 'Loading stock…')}
                skeletonRows={4}
              />
            ) : stockSummary.length === 0 ? (
              <p className="text-sm text-slate-600">
                {t('backoffice.inventory_stock_empty', 'No products in stock.')}
              </p>
            ) : (
              <>
                {isAllScope ? (
                  <div className="space-y-3">
                    {stockDepartmentsGrouped.map((dep) => (
                      <div
                        key={dep.departmentId}
                        className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-slate-900/5 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                            {dep.departmentName}
                          </span>
                          <span className="text-[11px] text-slate-500">/{dep.departmentSlug}</span>
                        </div>
                        <p className="mb-3 mt-2 px-4 text-xs text-slate-700">
                          Products {dep.productsCount} • Weight {dep.totalWeightLbs.toFixed(2)} lb • Stock sale value {formatPrice(dep.totalStockSaleValue)}
                        </p>
                        <div className="overflow-x-auto border-t border-slate-200">
                          <table className="min-w-full text-left text-xs sm:text-sm">
                            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                              <tr>
                                <th className="w-14 py-3 pl-3 pr-2">Image</th>
                                <th className="py-3 pr-4">Product</th>
                                <th className="py-3 pr-4">Category</th>
                                <th className="py-3 pr-4">SKU</th>
                                <th className="py-3 pr-4">Last updated</th>
                                <th className="py-3 pr-4">Weight (lb)</th>
                                <th className="py-3 pr-4">Value</th>
                                <th className="py-3 pr-4">Sale price / unit</th>
                                <th className="py-3 pr-4">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 bg-white">
                              {dep.rows.map((row) => {
                                const imageSrc =
                                  row.imageUrl && row.imageUrl.startsWith('http')
                                    ? row.imageUrl
                                    : row.imageUrl
                                      ? `${API_BASE}${row.imageUrl.startsWith('/') ? '' : '/'}${row.imageUrl}`
                                      : null;
                                const effectiveSalePrice = row.salePrice ?? row.pricePerUnit;
                                const value = Number(row.currentStockLbs || 0) * Number(effectiveSalePrice || 0);
                                return (
                                  <tr
                                    key={row.id}
                                    className="cursor-pointer hover:bg-indigo-50/40"
                                    onClick={() => switchWorkspaceScope(dep.departmentSlug)}
                                  >
                                    <td className="w-14 py-2 pl-3 pr-2 align-middle">
                                      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-50">
                                        {imageSrc ? (
                                          <img src={imageSrc} alt="" className="h-full w-full object-cover" />
                                        ) : (
                                          <span className="text-[10px] font-medium text-slate-300">—</span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="py-2 pr-4 font-semibold text-slate-900">{row.name}</td>
                                    <td className="py-2 pr-4 text-slate-700">{row.category?.name || 'Uncategorized'}</td>
                                    <td className="py-2 pr-4 font-mono text-[11px] text-slate-700">{row.sku}</td>
                                    <td className="py-2 pr-4 whitespace-nowrap text-slate-700">
                                      {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}
                                    </td>
                                    <td className="py-2 pr-4 text-slate-700">{row.currentStockLbs.toFixed(2)}</td>
                                    <td className="py-2 pr-4 font-semibold text-slate-900 whitespace-nowrap">{formatPrice(value)}</td>
                                    <td className="py-2 pr-4 text-slate-700">
                                      {row.salePrice != null
                                        ? `${currency} ${formatPrice(Number(row.salePrice))} / ${row.baseUnit}`
                                        : '—'}
                                    </td>
                                    <td className="py-2 pr-4">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          switchWorkspaceScope(dep.departmentSlug);
                                        }}
                                        className="text-xs font-medium text-brand-600 hover:underline"
                                      >
                                        Switch
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                <div className="space-y-3">
                  {stockCategoryList.map((cat) => {
                    const isOpen = openStockCategoryId === cat.id;
                    return (
                      <div
                        key={cat.id}
                        className={[
                          'overflow-hidden rounded-xl border bg-white shadow-sm',
                          isOpen ? 'border-brand-300 ring-2 ring-brand-200/50' : 'border-slate-200',
                        ].join(' ')}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50/70 px-4 py-3">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="truncate text-sm font-semibold text-slate-900">
                                {cat.name}
                              </p>
                              <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                                {cat.count} products
                              </span>
                              {cat.latestUpdatedAt ? (
                                <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                                  Updated {new Date(cat.latestUpdatedAt).toLocaleString()}
                                </span>
                              ) : null}
                            </div>
                            <p className="mt-1 text-xs text-slate-600">
                              Weight {cat.totalWeightLbs.toFixed(2)} lb • Value {formatPrice(cat.totalValue)}
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() => setOpenStockCategoryId((prev) => (prev === cat.id ? null : cat.id))}
                            className={[
                              'rounded-full px-4 py-2 text-[11px] font-semibold uppercase tracking-wide shadow-sm',
                              isOpen
                                ? 'border border-brand-200 bg-white text-brand-700 hover:bg-brand-50'
                                : 'border border-slate-300 bg-white text-slate-700 hover:border-slate-400',
                            ].join(' ')}
                          >
                            {isOpen ? 'Hide details' : 'Details'}
                          </button>
                        </div>

                        {isOpen && (
                          <div className="border-t border-slate-200 p-4">
                            <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                              <span className="rounded-full bg-slate-900/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                                Category weight: {cat.totalWeightLbs.toFixed(2)} lb
                              </span>
                              <span className="rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-800">
                                Category value: {formatPrice(cat.totalValue)}
                              </span>
                            </div>

                            <div className="overflow-x-auto rounded-xl border border-slate-200">
                              <table className="min-w-full text-left text-xs sm:text-sm">
                                <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                                  <tr>
                                    <th className="w-14 py-3 pl-3 pr-2">
                                      {t('backoffice.inventory_stock_col_image', 'Image')}
                                    </th>
                                    <th className="py-3 pr-4">
                                      {t('backoffice.inventory_stock_col_product', 'Product')}
                                    </th>
                                    <th className="py-3 pr-4">SKU</th>
                                    <th className="py-3 pr-4">
                                      {t('backoffice.inventory_stock_col_updated', 'Last updated')}
                                    </th>
                                    <th className="py-3 pr-4">Weight (lb)</th>
                                    <th className="py-3 pr-4">Value</th>
                                    <th className="py-3 pr-4">
                                      {t('backoffice.inventory_stock_col_sale_price', 'Sale price')} / unit
                                    </th>
                                    <th className="py-3 pr-4">
                                      {t('backoffice.inventory_stock_col_actions', 'Actions')}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 bg-white">
                                  {cat.products
                                    .slice()
                                    .sort((a, b) => b.currentStockLbs - a.currentStockLbs || a.name.localeCompare(b.name))
                                    .map((row) => {
                                      const imageSrc =
                                        row.imageUrl && row.imageUrl.startsWith('http')
                                          ? row.imageUrl
                                          : row.imageUrl
                                            ? `${API_BASE}${row.imageUrl.startsWith('/') ? '' : '/'}${row.imageUrl}`
                                            : null;
                                      const effectiveSalePrice = row.salePrice ?? row.pricePerUnit;
                                      const value = Number(row.currentStockLbs || 0) * Number(effectiveSalePrice || 0);
                                      return (
                                        <tr key={row.id} className="hover:bg-slate-50/40">
                                          <td className="w-14 py-2 pl-3 pr-2 align-middle">
                                            <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-50">
                                              {imageSrc ? (
                                                <img src={imageSrc} alt="" className="h-full w-full object-cover" />
                                              ) : (
                                                <span className="text-slate-300 text-[10px] font-medium">—</span>
                                              )}
                                            </div>
                                          </td>
                                          <td className="py-2 pr-4 text-slate-900 font-semibold">{row.name}</td>
                                          <td className="py-2 pr-4 text-slate-700 font-mono text-[11px]">{row.sku}</td>
                                          <td className="py-2 pr-4 text-slate-700 whitespace-nowrap">
                                            {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}
                                          </td>
                                          <td className="py-2 pr-4 text-slate-700">
                                            {row.currentStockLbs.toFixed(2)}
                                          </td>
                                          <td className="py-2 pr-4 font-semibold text-slate-900 whitespace-nowrap">
                                            {formatPrice(value)}
                                          </td>
                                          <td className="py-2 pr-4 text-slate-700">
                                            {editingSalePriceId === row.id ? (
                                              <span className="flex items-center gap-1">
                                                <input
                                                  type="number"
                                                  min="0"
                                                  step="0.01"
                                                  value={editingSalePriceValue}
                                                  onChange={(e) => setEditingSalePriceValue(e.target.value)}
                                                  className="w-20 rounded border border-slate-300 px-2 py-1 text-xs"
                                                />
                                                <button
                                                  type="button"
                                                  disabled={salePriceSaving}
                                                  onClick={() => setSalePrice(row.id, editingSalePriceValue)}
                                                  className="text-brand-600 text-xs font-medium"
                                                >
                                                  {t('common.save', 'Save')}
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    setEditingSalePriceId(null);
                                                    setEditingSalePriceValue('');
                                                  }}
                                                  className="text-slate-500 text-xs"
                                                >
                                                  {t('common.cancel', 'Cancel')}
                                                </button>
                                              </span>
                                            ) : (
                                              <span className="flex items-center gap-1">
                                                {row.salePrice != null
                                                  ? `${currency} ${formatPrice(Number(row.salePrice))} / ${row.baseUnit}`
                                                  : '—'}
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    setEditingSalePriceId(row.id);
                                                    setEditingSalePriceValue(
                                                      row.salePrice != null ? String(row.salePrice) : '',
                                                    );
                                                  }}
                                                  className="text-brand-600 text-xs hover:underline"
                                                >
                                                  {t('backoffice.inventory_stock_set_sale_price', 'Set')}
                                                </button>
                                              </span>
                                            )}
                                          </td>
                                          <td className="py-2 pr-4">
                                            <button
                                              type="button"
                                              onClick={() => openLotsModal({ id: row.id, name: row.name, sku: row.sku })}
                                              className="text-brand-600 text-xs font-medium hover:underline"
                                            >
                                              {t('backoffice.inventory_stock_view_lots', 'View lots')}
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                )}
                {stockTotalPages > 1 && (
                  <div className="mt-4 flex flex-col items-start gap-2 text-xs text-slate-600 sm:flex-row sm:items-center sm:justify-end">
                    <button
                      type="button"
                      disabled={stockPage <= 1}
                      onClick={() => loadStockSummary(stockPage - 1, stockSearch)}
                      className="rounded-full border border-slate-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {t('backoffice.inventory_prev_page', 'Prev')}
                    </button>
                    <span>
                      {stockPage} / {stockTotalPages}
                    </span>
                    <button
                      type="button"
                      disabled={stockPage >= stockTotalPages}
                      onClick={() => loadStockSummary(stockPage + 1, stockSearch)}
                      className="rounded-full border border-slate-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {t('backoffice.inventory_next_page', 'Next')}
                    </button>
                  </div>
                )}
              </>
            )}

            {lotsModalProduct && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                onClick={() => setLotsModalProduct(null)}
                role="dialog"
                aria-modal="true"
              >
                <div
                  className="max-h-[80vh] w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                    <h3 className="text-base font-semibold text-slate-900">
                      {t('backoffice.inventory_lots_modal_title', 'Lots for')} {lotsModalProduct.name} ({lotsModalProduct.sku})
                    </h3>
                    <button
                      type="button"
                      onClick={() => setLotsModalProduct(null)}
                      className="rounded p-1 text-slate-500 hover:bg-slate-100"
                    >
                      ×
                    </button>
                  </div>
                  <div className="overflow-auto max-h-[60vh] p-4">
                    {lotsModalLoading ? (
                      <PaginatedListLoading
                        compact
                        minHeightClass="min-h-[160px]"
                        title={t('backoffice.inventory_lots_loading', 'Loading…')}
                      />
                    ) : lotsForProduct.length === 0 ? (
                      <p className="text-sm text-slate-600">{t('backoffice.inventory_lots_empty', 'No lots.')}</p>
                    ) : (
                      <table className="min-w-full text-left text-xs sm:text-sm">
                        <thead className="border-b border-slate-200 text-xs uppercase text-slate-500">
                          <tr>
                            <th className="py-2 pr-4">{t('backoffice.inventory_lots_col_batch', 'Batch')}</th>
                            <th className="py-2 pr-4">{t('backoffice.inventory_lots_col_purchased', 'Purchased (lb)')}</th>
                            <th className="py-2 pr-4">{t('backoffice.inventory_lots_col_remaining', 'Remaining (lb)')}</th>
                            <th className="py-2 pr-4">{t('backoffice.inventory_lots_col_expiry', 'Expiry')}</th>
                            <th className="py-2 pr-2 text-right">{t('backoffice.inventory_lots_col_actions', 'Actions')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {lotsForProduct.map((lot) => (
                            <tr key={lot.id}>
                              <td className="py-2 pr-4">{lot.batchCode || '—'}</td>
                              <td className="py-2 pr-4">{lot.purchasedQtyLbs.toFixed(2)}</td>
                              <td className="py-2 pr-4">{lot.remainingQtyLbs.toFixed(2)}</td>
                              <td className="py-2 pr-4">
                                {editingLotExpiryId === lot.id ? (
                                  <span className="flex items-center gap-2">
                                    <input
                                      type="date"
                                      value={editingLotExpiryValue}
                                      onChange={(e) => setEditingLotExpiryValue(e.target.value)}
                                      className="h-9 rounded border border-slate-300 px-2 text-xs"
                                    />
                                    <button
                                      type="button"
                                      disabled={lotExpirySaving}
                                      onClick={() => saveLotExpiry(lot.id, editingLotExpiryValue)}
                                      className="text-brand-600 text-xs font-semibold"
                                    >
                                      {t('common.save', 'Save')}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingLotExpiryId(null);
                                        setEditingLotExpiryValue('');
                                      }}
                                      className="text-slate-500 text-xs"
                                    >
                                      {t('common.cancel', 'Cancel')}
                                    </button>
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-2">
                                    <span>
                                      {lot.expiryDate ? new Date(lot.expiryDate).toLocaleDateString() : '—'}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingLotExpiryId(lot.id);
                                        setEditingLotExpiryValue(
                                          lot.expiryDate ? new Date(lot.expiryDate).toISOString().slice(0, 10) : '',
                                        );
                                      }}
                                      className="text-brand-600 text-xs font-semibold hover:underline"
                                    >
                                      {t('backoffice.inventory_lots_edit_expiry', 'Edit')}
                                    </button>
                                  </span>
                                )}
                              </td>
                              <td className="py-2 pr-2 text-right">
                                {editingLotExpiryId === lot.id ? (
                                  <button
                                    type="button"
                                    onClick={() => saveLotExpiry(lot.id, '')}
                                    disabled={lotExpirySaving}
                                    className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-rose-700 hover:bg-rose-100 disabled:opacity-60"
                                  >
                                    Clear
                                  </button>
                                ) : (
                                  <span className="text-slate-300">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            )}

            {showExpiringSoon && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                onClick={() => setShowExpiringSoon(false)}
                role="dialog"
                aria-modal="true"
              >
                <div
                  className="max-h-[80vh] w-full max-w-3xl overflow-hidden rounded-xl bg-white shadow-xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">
                        Expiring soon
                      </h3>
                      <p className="text-xs text-slate-600">
                        Products with lots expiring within the next {expiryDaysBefore} day(s).
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowExpiringSoon(false)}
                      className="rounded p-1 text-slate-500 hover:bg-slate-100"
                    >
                      ×
                    </button>
                  </div>
                  <div className="max-h-[65vh] overflow-auto p-4">
                    {expiringSoon.error ? (
                      <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                        {expiringSoon.error}
                      </div>
                    ) : expiringSoon.loading ? (
                      <PaginatedListLoading
                        compact
                        minHeightClass="min-h-[140px]"
                        title={t('common.list_loading_title')}
                      />
                    ) : expiringSoon.productsCount === 0 ? (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center">
                        <p className="text-sm font-semibold text-slate-900">No products expiring soon.</p>
                        <p className="mt-1 text-xs text-slate-600">
                          Nothing expires within {expiryDaysBefore} day(s) based on current settings.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {expiringSoon.products.map((p) => (
                          <div
                            key={p.productId}
                            className="rounded-xl border-2 border-rose-100 bg-white p-3"
                          >
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="truncate text-sm font-semibold text-slate-900">
                                  {p.name}
                                </div>
                                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                                  <span className="rounded-full bg-slate-900/5 px-2 py-0.5 font-mono text-[11px] text-slate-700">
                                    {p.sku}
                                  </span>
                                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-900">
                                    Exp {p.earliestExpiry ? new Date(p.earliestExpiry).toLocaleDateString() : '—'}
                                  </span>
                                  <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                                    Lots {p.lotsCount}
                                  </span>
                                  <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-rose-800">
                                    Remaining {p.remainingQtyLbs.toFixed(2)} lb
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>
        ) : (
          <section className="workspace-panel">
            <div className="mb-5 space-y-3">
              <div className="grid gap-3 lg:grid-cols-[1fr_auto] lg:items-end">
                {!showNewPurchase ? (
                  <div className="flex w-full flex-wrap items-end gap-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="search"
                        placeholder={t('backoffice.inventory_purchases_search', 'Search by supplier or reference')}
                        value={purchasesSearch}
                        onChange={(e) => setPurchasesSearch(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70 sm:w-72"
                      />
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <div className="space-y-1">
                        <span className="block text-[11px] font-medium text-slate-600">
                          {t('backoffice.inventory_purchases_from', 'From')}
                        </span>
                        <input
                          type="date"
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                        />
                      </div>
                      <div className="space-y-1">
                        <span className="block text-[11px] font-medium text-slate-600">
                          {t('backoffice.inventory_purchases_to', 'To')}
                        </span>
                        <input
                          type="date"
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div />
                )}

                <div className="flex flex-wrap items-center justify-end gap-2">
                  <button
                    type="button"
                    disabled={isAllScope}
                    onClick={() => setShowNewPurchase((prev) => !prev)}
                    title={isAllScope ? 'Select a specific department scope to create a purchase.' : undefined}
                    className="rounded-full bg-brand-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {showNewPurchase
                      ? t('backoffice.inventory_new_purchase_hide', 'Hide form')
                      : t('backoffice.inventory_new_purchase_show', 'New purchase')}
                  </button>
                  <button
                    type="button"
                    onClick={() => loadPurchases(1, purchasesSearch, fromDate, toDate)}
                    className="rounded-md bg-slate-900 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-slate-800"
                  >
                    {t('backoffice.inventory_search_button', 'Search')}
                  </button>
                </div>
              </div>
              {isAllScope ? (
                <p className="text-xs font-medium text-amber-700">
                  Select a specific department scope to create a purchase.
                </p>
              ) : null}

              {!showNewPurchase && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Purchases
                    </div>
                    <div className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
                      {purchasesTotal}
                    </div>
                  </div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 shadow-sm">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                      Purchase cost (supplier)
                    </div>
                    <div className="mt-1 text-3xl font-semibold tracking-tight text-emerald-900">
                      {formatPrice(purchasesTotalCostSum)}
                    </div>
                    <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-800/80">
                      Entire purchases (filtered) • this is what you paid suppliers
                    </div>
                  </div>
                </div>
              )}
            </div>

            {showNewPurchase && !isAllScope && (
              <form
                className="mb-6 space-y-5 border-b border-slate-100 pb-6"
                onSubmit={handleNewPurchaseSubmit}
              >
                <div className="workspace-stat-card">
                  <div className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Purchase header
                  </div>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-slate-700" htmlFor="purchaseSupplier">
                        Supplier <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-1 space-y-1">
                        <div className="flex items-center gap-3">
                          {!isSelfSupplier ? (
                            <input
                              id="purchaseSupplier"
                              type="text"
                              value={supplierQuery}
                              onChange={(e) => {
                                setSupplierQuery(e.target.value);
                                setSupplierId(null);
                              }}
                              placeholder="Search supplier by name"
                              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40"
                            />
                          ) : (
                            <div className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                              Self supplier
                            </div>
                          )}
                          <label className="inline-flex items-center gap-2 whitespace-nowrap text-xs text-slate-700">
                            <input
                              type="checkbox"
                              checked={isSelfSupplier}
                              onChange={(e) => {
                                setIsSelfSupplier(e.target.checked);
                                if (e.target.checked) {
                                  setSupplierId(null);
                                  setSupplierQuery('');
                                  setSupplierOptions([]);
                                }
                              }}
                            />
                            We are our own supplier
                          </label>
                        </div>
                        {!isSelfSupplier && supplierSearchLoading ? (
                          <p className="text-[11px] text-slate-500">Loading suppliers…</p>
                        ) : null}
                        {!isSelfSupplier && supplierOptions.length > 0 ? (
                          <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white text-sm">
                            {supplierOptions.map((s) => (
                              <button
                                key={s.id}
                                type="button"
                                onClick={() => {
                                  setSupplierId(s.id);
                                  setSupplierQuery(s.name);
                                  setSupplierOptions([]);
                                }}
                                className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-slate-50"
                              >
                                <div className="min-w-0">
                                  <div className="truncate text-sm font-semibold text-slate-900">{s.name}</div>
                                  <div className="text-xs text-slate-500">
                                    {s.phone || '—'}
                                    {s.country ? ` • ${getCountryName(s.country)}` : ''}
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : null}
                        {newPurchaseErrors.supplier ? (
                          <p className="text-[11px] text-rose-600">{newPurchaseErrors.supplier}</p>
                        ) : null}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700" htmlFor="purchaseDate">
                        Purchase date <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="purchaseDate"
                        type="datetime-local"
                        value={purchaseDate}
                        onChange={(e) => setPurchaseDate(e.target.value)}
                        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40"
                      />
                      {newPurchaseErrors.purchaseDate ? (
                        <p className="mt-1 text-[11px] text-rose-600">{newPurchaseErrors.purchaseDate}</p>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="workspace-stat-card">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Purchase items</div>
                      <div className="mt-1 text-sm font-semibold text-slate-900">
                        {purchaseLines.length} line{purchaseLines.length === 1 ? '' : 's'}
                      </div>
                    </div>
                    <div className="rounded-full bg-slate-900/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                      Total: {currency}{' '}
                      {purchaseLines
                        .reduce((s, l) => s + l.quantity * l.unitPrice, 0)
                        .toFixed(2)}
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-12">
                    <div className="md:col-span-3">
                      <label className="block text-xs font-semibold text-slate-700">Product</label>
                      <input
                        value={productQuery}
                        onChange={(e) => {
                          setProductQuery(e.target.value);
                          setLineProductId('');
                        }}
                        placeholder="Search product by name or SKU"
                        className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40"
                      />
                      {productSearchLoading ? (
                        <p className="mt-1 text-[11px] text-slate-500">Searching…</p>
                      ) : null}
                      {productOptions.length > 0 ? (
                        <div className="mt-1 max-h-44 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                          {productOptions.map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => {
                                const alreadyInPurchase = purchaseLines.some((l) => l.productId === p.id);
                                if (alreadyInPurchase) {
                                  setDuplicatePick({ open: true, product: { id: p.id, name: p.name, sku: p.sku } });
                                  return;
                                }
                                setLineProductId(p.id);
                                setProductQuery(`${p.name} (${p.sku})`);
                                setProductOptions([]);
                              }}
                              className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-slate-50"
                            >
                              <div className="min-w-0">
                                <div className="truncate text-sm font-semibold text-slate-900">{p.name}</div>
                                <div className="text-xs font-mono text-slate-500">{p.sku}</div>
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : null}
                      {newPurchaseErrors.product ? (
                        <p className="mt-1 text-[11px] text-rose-600">{newPurchaseErrors.product}</p>
                      ) : null}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-slate-700">Qty</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={lineQty}
                        onChange={(e) => setLineQty(e.target.value)}
                        className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40"
                      />
                    </div>

                    <div className="md:col-span-1">
                      <label className="block text-xs font-semibold text-slate-700">Unit</label>
                      <select
                        value={lineUnit}
                        onChange={(e) => setLineUnit((e.target.value as PurchaseLineUnit) || 'LB')}
                        className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40"
                      >
                        {departmentUnits.map((unit) => (
                          <option key={unit} value={unit}>{unit}</option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-slate-700">Unit cost ({currency})</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={linePrice}
                        onChange={(e) => setLinePrice(e.target.value)}
                        className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-slate-700">Expiry (optional)</label>
                      <input
                        type="date"
                        value={lineExpiry}
                        onChange={(e) => setLineExpiry(e.target.value)}
                        className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <span className="block text-xs font-semibold text-transparent select-none">Action</span>
                      <button
                        type="button"
                        onClick={() => {
                          const qty = Number(lineQty);
                          const price = Number(linePrice);
                          if (!lineProductId) {
                            setNewPurchaseErrors((p) => ({ ...p, product: 'Please pick a product.' }));
                            return;
                          }
                          if (!Number.isFinite(qty) || qty <= 0) {
                            setNewPurchaseErrors((p) => ({ ...p, quantity: 'Quantity must be greater than zero.' }));
                            return;
                          }
                          if (!Number.isFinite(price) || price <= 0) {
                            setNewPurchaseErrors((p) => ({ ...p, unitPrice: 'Unit cost must be greater than zero.' }));
                            return;
                          }
                          setNewPurchaseErrors((p) => ({ ...p, product: undefined, quantity: undefined, unitPrice: undefined }));
                          setPurchaseLines((prev) => ([
                            ...prev,
                            {
                              productId: lineProductId,
                              productName: productQuery.trim() || 'Product',
                              quantity: qty,
                              unit: lineUnit,
                              unitPrice: price,
                              expiryDate: lineExpiry || undefined,
                              batchCode: lineBatch.trim() || undefined,
                            },
                          ]));
                          setLineProductId('');
                          setProductQuery('');
                          setLineQty('');
                          setLinePrice('');
                          setLineExpiry('');
                          setLineBatch('');
                        }}
                        className="mt-1 inline-flex h-10 w-full items-center justify-center rounded-lg bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-400/40"
                      >
                        Add item
                      </button>
                    </div>
                  </div>

                  {(newPurchaseErrors.quantity || newPurchaseErrors.unitPrice) ? (
                    <div className="mt-2 text-[11px] text-rose-600">
                      {newPurchaseErrors.quantity || newPurchaseErrors.unitPrice}
                    </div>
                  ) : null}

                  {purchaseLines.length > 0 ? (
                    <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200">
                      <table className="min-w-full text-left text-xs">
                        <thead className="bg-slate-50 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                          <tr>
                            <th className="px-3 py-2">Product</th>
                            <th className="px-3 py-2">Qty</th>
                            <th className="px-3 py-2">Unit cost</th>
                            <th className="px-3 py-2">Expiry</th>
                            <th className="px-3 py-2">Batch</th>
                            <th className="px-3 py-2 text-right">Line total</th>
                            <th className="px-3 py-2" />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {purchaseLines.map((l, idx) => (
                            <tr key={`${l.productId}:${idx}`}>
                              <td className="px-3 py-2 text-slate-900 font-semibold">{l.productName}</td>
                              <td className="px-3 py-2 text-slate-700">
                                {l.quantity} {l.unit}
                              </td>
                              <td className="px-3 py-2 text-slate-700">
                                {currency} {l.unitPrice.toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-slate-700">{l.expiryDate || '—'}</td>
                              <td className="px-3 py-2 text-slate-700">{l.batchCode || '—'}</td>
                              <td className="px-3 py-2 text-right font-semibold text-slate-900">
                                {currency} {(l.quantity * l.unitPrice).toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <button
                                  type="button"
                                  onClick={() => setPurchaseLines((prev) => prev.filter((_, i) => i !== idx))}
                                  className="rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-rose-700 hover:bg-rose-100"
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="mt-3 text-xs text-slate-500">Add at least one item line to record lots and expiry dates.</p>
                  )}
                </div>

                {duplicatePick.open && duplicatePick.product ? (
                  <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
                    role="dialog"
                    aria-modal="true"
                  >
                    <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-xl">
                      <div className="px-5 pt-5">
                        <div className="text-sm font-semibold text-slate-900">
                          Product already added
                        </div>
                        <p className="mt-2 text-sm text-slate-600">
                          <span className="font-semibold text-slate-800">
                            {duplicatePick.product.name}
                          </span>{' '}
                          ({duplicatePick.product.sku}) is already in this new purchase list.
                          Do you want to add it again?
                        </p>
                      </div>
                      <div className="mt-5 flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
                        <button
                          type="button"
                          onClick={() => setDuplicatePick({ open: false, product: null })}
                          className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:border-slate-400"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const p = duplicatePick.product!;
                            setLineProductId(p.id);
                            setProductQuery(`${p.name} (${p.sku})`);
                            setProductOptions([]);
                            setDuplicatePick({ open: false, product: null });
                          }}
                          className="rounded-lg bg-brand-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-brand-500"
                        >
                          Add anyway
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="flex flex-wrap items-center justify-between gap-3">
                  {newPurchaseError ? (
                    <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                      {newPurchaseError}
                    </p>
                  ) : null}
                  <div className="ml-auto flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewPurchase(false);
                        setNewPurchaseError(null);
                        setNewPurchaseErrors({});
                        setPurchaseLines([]);
                        setProductQuery('');
                        setLineProductId('');
                        setLineQty('');
                        setLinePrice('');
                        setLineExpiry('');
                        setLineBatch('');
                      }}
                      className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:border-slate-400"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={newPurchaseSaving}
                      className="rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
                    >
                      {newPurchaseSaving ? 'Saving…' : 'Save purchase'}
                    </button>
                  </div>
                </div>
              </form>
            )}

            {purchasesLoading ? (
              <PaginatedListLoading
                title={t('backoffice.inventory_purchases_loading', 'Loading purchases…')}
                skeletonRows={4}
              />
            ) : purchases.length === 0 ? (
              <p className="text-sm text-slate-600">
                {t('backoffice.inventory_purchases_empty', 'No purchases found for the selected filters.')}
              </p>
            ) : (
              <>
                {isAllScope ? (
                  <div className="space-y-3">
                    {purchaseDepartmentsGrouped.map((dep) => (
                      <div
                        key={dep.departmentId}
                        className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-slate-900/5 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                            {dep.departmentName}
                          </span>
                          <span className="text-[11px] text-slate-500">/{dep.departmentSlug}</span>
                        </div>
                        <p className="mb-3 mt-2 px-4 text-xs text-slate-700">
                          Purchases {dep.purchasesCount} • Total purchase cost {formatPrice(dep.totalPurchaseCost)}
                        </p>
                        <div className="overflow-x-auto border-t border-slate-200">
                          <table className="min-w-full text-left text-[11px] sm:text-xs">
                            <thead className="border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-600">
                              <tr>
                                <th className="py-3 pl-4 pr-4">Time</th>
                                <th className="py-3 pr-4">Supplier</th>
                                <th className="py-3 pr-4">Reference</th>
                                <th className="py-3 pr-4">Item</th>
                                <th className="py-3 pr-4">Total</th>
                                <th className="py-3 pr-4">Action</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                              {dep.rows.map((p) => {
                                const first = p.items[0] || null;
                                const extraCount = Math.max(0, p.items.length - 1);
                                return (
                                  <tr
                                    key={p.id}
                                    className="cursor-pointer bg-white hover:bg-indigo-50/40"
                                    onClick={() => switchWorkspaceScope(dep.departmentSlug)}
                                  >
                                    <td className="py-3 pl-4 pr-4 text-slate-900">
                                      {new Date(p.purchasedAt).toLocaleString()}
                                    </td>
                                    <td className="py-3 pr-4 text-slate-800">{p.supplierName || '—'}</td>
                                    <td className="py-3 pr-4 text-slate-700">
                                      <span className="rounded-md bg-slate-100 px-2 py-1 font-mono text-[11px] text-slate-700">
                                        {p.reference || '—'}
                                      </span>
                                    </td>
                                    <td className="py-3 pr-4 text-slate-800">
                                      {first ? (
                                        <div className="flex flex-wrap items-center gap-2">
                                          <span className="font-semibold text-slate-900">{first.product.name}</span>
                                          <span className="text-slate-600">
                                            {first.quantityLbs.toFixed(2)} lb @ {currency} {formatPrice(Number(first.unitPrice))}
                                          </span>
                                          {extraCount > 0 && (
                                            <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-800">
                                              +{extraCount} more
                                            </span>
                                          )}
                                        </div>
                                      ) : '—'}
                                    </td>
                                    <td className="py-3 pr-4 font-semibold text-slate-900">{Number(p.totalCost).toFixed(2)}</td>
                                    <td className="py-3 pr-4">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          switchWorkspaceScope(dep.departmentSlug);
                                        }}
                                        className="text-xs font-medium text-brand-600 hover:underline"
                                      >
                                        Switch
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                <div className="overflow-x-auto">
                  {Object.entries(
                    purchases.reduce<Record<string, PurchaseRow[]>>((acc, p) => {
                      const key = new Date(p.purchasedAt).toLocaleDateString();
                      if (!acc[key]) acc[key] = [];
                      acc[key].push(p);
                      return acc;
                    }, {}),
                  ).map(([dateLabel, group]) => (
                    <div
                      key={dateLabel}
                      className={[
                        'mb-4 overflow-hidden rounded-xl border bg-white shadow-sm',
                        openPurchaseDate === dateLabel ? 'border-brand-300 ring-2 ring-brand-200/50' : 'border-slate-200',
                      ].join(' ')}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setOpenPurchaseId(null);
                          setOpenPurchaseDate((prev) => (prev === dateLabel ? null : dateLabel));
                        }}
                        className={[
                          'flex w-full items-center justify-between gap-3 px-4 py-3 text-left',
                          openPurchaseDate === dateLabel ? 'bg-brand-50/60' : 'bg-slate-50/60 hover:bg-slate-50',
                        ].join(' ')}
                      >
                        <span className="flex items-center gap-2">
                          <span
                            className={[
                              'rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide',
                              openPurchaseDate === dateLabel
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-slate-200 text-slate-700',
                            ].join(' ')}
                          >
                            {openPurchaseDate === dateLabel ? 'Open' : 'Closed'}
                          </span>
                          <span className="text-sm font-semibold tracking-tight text-slate-900">
                            {dateLabel}
                          </span>
                        </span>
                        <span className="flex items-center gap-3">
                          <span className="rounded-full bg-white/70 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                            {t('backoffice.inventory_purchases_group_total', 'Purchases')}{' '}
                            {group.length}
                          </span>
                          <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-800">
                            {currency}{' '}
                            {formatPrice(group.reduce((s, p) => s + Number(p.totalCost || 0), 0))}
                          </span>
                          <span
                            className={[
                              'grid h-7 w-7 place-items-center rounded-full border text-slate-600',
                              openPurchaseDate === dateLabel ? 'border-brand-200 bg-white' : 'border-slate-200 bg-white',
                            ].join(' ')}
                            aria-hidden="true"
                          >
                            {openPurchaseDate === dateLabel ? '▾' : '▸'}
                          </span>
                        </span>
                      </button>

                      {openPurchaseDate === dateLabel && (
                        <div className="border-t border-slate-200 bg-white">
                        <table className="min-w-full text-left text-[11px] sm:text-xs">
                          <thead className="border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-600">
                            <tr>
                              <th className="py-3 pl-4 pr-4">
                                {t('backoffice.inventory_purchases_col_time', 'Time')}
                              </th>
                              <th className="py-3 pr-4">
                                {t('backoffice.inventory_purchases_col_supplier', 'Supplier')}
                              </th>
                              <th className="py-3 pr-4">
                                {t('backoffice.inventory_purchases_col_ref', 'Reference')}
                              </th>
                              <th className="py-3 pr-4">Item</th>
                              <th className="py-3 pr-4">
                                {t('backoffice.inventory_purchases_col_total', 'Total')}
                              </th>
                              <th className="py-3 pr-4" />
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {group.map((p) => {
                              const first = p.items[0] || null;
                              const extraCount = Math.max(0, p.items.length - 1);
                              const expanded = openPurchaseId === p.id;
                              return (
                                <React.Fragment key={p.id}>
                                  <tr
                                    className={[
                                      expanded ? 'bg-brand-50/40' : 'bg-white',
                                      'hover:bg-slate-50/40',
                                    ].join(' ')}
                                  >
                                    <td className="py-3 pl-4 pr-4 text-slate-900">
                                      {new Date(p.purchasedAt).toLocaleTimeString()}
                                    </td>
                                    <td className="py-3 pr-4 text-slate-800">
                                      {p.supplierName || '—'}
                                    </td>
                                    <td className="py-3 pr-4 text-slate-700">
                                      <span className="rounded-md bg-slate-100 px-2 py-1 font-mono text-[11px] text-slate-700">
                                        {p.reference || '—'}
                                      </span>
                                    </td>
                                    <td className="py-3 pr-4 text-slate-800">
                                      {first ? (
                                        <div className="flex flex-wrap items-center gap-2">
                                          <span className="font-semibold text-slate-900">
                                            {first.product.name}
                                          </span>
                                          <span className="text-slate-600">
                                            {first.quantityLbs.toFixed(2)} lb @ {currency}{' '}
                                            {formatPrice(Number(first.unitPrice))}
                                          </span>
                                          {extraCount > 0 && (
                                            <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-800">
                                              +{extraCount} more
                                            </span>
                                          )}
                                        </div>
                                      ) : (
                                        '—'
                                      )}
                                    </td>
                                    <td className="py-3 pr-4 font-semibold text-slate-900">
                                      {Number(p.totalCost).toFixed(2)}
                                    </td>
                                    <td className="py-3 pr-4 text-right">
                                      <button
                                        type="button"
                                        onClick={() => setOpenPurchaseId((prev) => (prev === p.id ? null : p.id))}
                                        className={[
                                          'rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide shadow-sm',
                                          expanded
                                            ? 'border border-brand-200 bg-white text-brand-700 hover:bg-brand-50'
                                            : 'border border-slate-300 bg-white text-slate-700 hover:border-slate-400',
                                        ].join(' ')}
                                      >
                                        {expanded ? 'Collapse' : 'Expand'}
                                      </button>
                                    </td>
                                  </tr>

                                  {expanded && (
                                    <tr>
                                      <td colSpan={6} className="bg-brand-50/20 pb-4">
                                        <div className="px-4">
                                          <div className="mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                                            <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                                              Items ({p.items.length})
                                            </div>
                                            <div className="p-3">
                                              <div className="grid gap-2 sm:grid-cols-2">
                                                {p.items.map((item) => (
                                                  <div
                                                    key={item.id}
                                                    className="flex flex-col gap-1 rounded-lg border-2 border-slate-100 bg-white px-3 py-2"
                                                  >
                                                    <div className="flex items-baseline justify-between gap-3">
                                                      <span className="font-semibold text-slate-800">
                                                        {item.product.name}
                                                      </span>
                                                      <span className="whitespace-nowrap text-slate-600">
                                                        {item.quantityLbs.toFixed(2)} lb @ {currency}{' '}
                                                        {formatPrice(Number(item.unitPrice))}
                                                      </span>
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-wide">
                                                      {item.expiryDate ? (
                                                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-900">
                                                          Exp {new Date(item.expiryDate).toLocaleDateString()}
                                                        </span>
                                                      ) : (
                                                        <span className="rounded-full bg-slate-200 px-2 py-0.5 text-slate-700">
                                                          No expiry
                                                        </span>
                                                      )}
                                                      {item.lot?.batchCode ? (
                                                        <span className="rounded-full bg-slate-900/5 px-2 py-0.5 text-slate-800">
                                                          Batch {item.lot.batchCode}
                                                        </span>
                                                      ) : null}
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                )}
                {purchasesTotalPages > 1 && (
                <div className="mt-4 flex flex-col items-start gap-2 text-xs text-slate-600 sm:flex-row sm:items-center sm:justify-end">
                    <button
                      type="button"
                      disabled={purchasesPage <= 1}
                      onClick={() => loadPurchases(purchasesPage - 1, purchasesSearch, fromDate, toDate)}
                      className="rounded-full border border-slate-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {t('backoffice.inventory_prev_page', 'Prev')}
                    </button>
                    <span>
                      {purchasesPage} / {purchasesTotalPages}
                    </span>
                    <button
                      type="button"
                      disabled={purchasesPage >= purchasesTotalPages}
                      onClick={() => loadPurchases(purchasesPage + 1, purchasesSearch, fromDate, toDate)}
                      className="rounded-full border border-slate-300 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {t('backoffice.inventory_next_page', 'Next')}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        )}
      </main>
    </MainLayout>
  );
}

export default InventoryPage;

